import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useEffect, useRef, useState } from 'react';
import { Alert } from '../components/alert';
import { ConfirmationDialog } from '../components/confirmation-dialog';
import { Loader } from '../components/loader';
import { Toast } from '../components/toast';
// For godMode animation
const shakeAnimation = `
  @keyframes shake {
    0% { transform: translate(1px, 1px) rotate(0deg); }
    10% { transform: translate(-1px, -2px) rotate(-3deg); }
    20% { transform: translate(-3px, 0px) rotate(3deg); }
    30% { transform: translate(3px, 2px) rotate(0deg); }
    40% { transform: translate(1px, -1px) rotate(3deg); }
    50% { transform: translate(-1px, 2px) rotate(-3deg); }
    60% { transform: translate(-3px, 1px) rotate(0deg); }
    70% { transform: translate(3px, 1px) rotate(-3deg); }
    80% { transform: translate(-1px, -1px) rotate(3deg); }
    90% { transform: translate(1px, 2px) rotate(0deg); }
    100% { transform: translate(1px, -2px) rotate(-3deg); }
  }
`;
export const SourContext = createContext({
    application: 'admin-portal',
    isLoading: false,
    godModeActive: false,
    isDesktop: false,
    setIsLoading: () => {
        return;
    },
    setShowAlert: () => {
        return;
    },
    setShowToast: () => {
        return;
    },
    openConfirmationDialog: () => {
        return;
    },
});
const SourContextProvider = ({ children, application: passedApplication, isAppAdmin, }) => {
    const sequence = useRef([]);
    const application = passedApplication;
    const [isLoading, setIsLoading] = useState(false);
    const [isToastActive, activateToast] = useState(false);
    const [isAlertActive, activateAlert] = useState(false);
    const [confirmationConfig, setConfirmationConfig] = useState({
        title: '',
        message: '',
        actionCallback: () => { },
    });
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [toastProps, setToastProps] = useState({});
    const [alertProps, setAlertProps] = useState({});
    const [message, setMessage] = useState(undefined);
    const [godModeAnimation, setGodModeAnimation] = useState(false);
    const [godModeActive, setGodModeActive] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window?.innerWidth >= 1024);
    const handleSetIsLoading = (opts) => {
        if (typeof opts == 'boolean') {
            setMessage(undefined);
            setIsLoading(opts);
        }
        else if (typeof opts == 'object') {
            const { isLoading, message } = opts;
            setMessage(message);
            setIsLoading(isLoading);
        }
        else {
            // no paramater, we treat it as toggle... but in typescript this should complain and never be ran.
            setMessage(undefined);
            setIsLoading(!isLoading);
        }
    };
    const setShowAlert = (opts) => {
        setAlertProps(opts);
        activateAlert(true);
    };
    const setShowToast = (opts) => {
        setToastProps(opts);
        activateToast(true);
    };
    const openConfirmationDialog = ({ title, message, actionCallback, confirmText, cancelText, isDelete, icon, }) => {
        setShowConfirmationDialog(true);
        setConfirmationConfig({
            title,
            message,
            actionCallback,
            confirmText,
            cancelText,
            isDelete,
            icon,
        });
    };
    const resetDialog = () => {
        setShowConfirmationDialog(false);
        setConfirmationConfig({
            title: '',
            message: '',
            actionCallback: () => { },
        });
    };
    const onConfirm = () => {
        resetDialog();
        confirmationConfig.actionCallback(true);
    };
    const onDismiss = () => {
        resetDialog();
        confirmationConfig.actionCallback(false);
    };
    // For godMode
    useEffect(() => {
        const handleKeyDown = (event) => {
            sequence.current.push(event.key);
            if (sequence.current.length === 8 &&
                sequence.current[0] === 'ArrowUp' &&
                sequence.current[1] === 'ArrowDown' &&
                sequence.current[2] === 'ArrowUp' &&
                sequence.current[3] === 'ArrowDown' &&
                sequence.current[4] === 'a' &&
                sequence.current[5] === 'b' &&
                sequence.current[6] === 'a' &&
                sequence.current[7] === 'b') {
                if (isAppAdmin) {
                    setGodModeAnimation(true);
                    setGodModeActive(true);
                    setTimeout(() => {
                        setGodModeAnimation(false);
                    }, 2000);
                }
                else {
                    document.body.style.animation = 'spin 2s linear infinite';
                }
                sequence.current = [];
            }
            if (sequence.current.length > 8) {
                sequence.current = [];
            }
        };
        document.body.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.removeEventListener('keydown', handleKeyDown);
        };
    }, [isAppAdmin]);
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window?.innerWidth >= 1024);
        };
        window?.addEventListener('resize', handleResize);
        return () => window?.removeEventListener('resize', handleResize);
    }, []);
    return (_jsxs(SourContext.Provider, { value: {
            isLoading,
            setIsLoading: handleSetIsLoading,
            setShowAlert,
            setShowToast,
            openConfirmationDialog,
            application,
            godModeActive,
            isDesktop,
        }, children: [children, _jsx(Alert, { closeAlert: () => activateAlert(false), isAlertActive: isAlertActive, alertProps: alertProps }), _jsx(Toast, { isToastActive: isToastActive, closeToast: () => activateToast(false), toastProps: toastProps }), _jsx(Loader, { isLoading: isLoading, message: message }), _jsx(ConfirmationDialog, { open: showConfirmationDialog, title: confirmationConfig.title, message: confirmationConfig.message, cancelText: confirmationConfig.cancelText, confirmText: confirmationConfig.confirmText, isDelete: confirmationConfig.isDelete, onConfirm: onConfirm, onDismiss: onDismiss, icon: confirmationConfig.icon }), godModeAnimation && (_jsx("div", { className: "fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70 text-5xl", children: _jsxs("div", { style: {
                        animation: 'shake 0.5s cubic-bezier(.36,.07,.19,.97) both',
                        animationIterationCount: '100',
                        color: '#FF4500',
                    }, children: [_jsx("style", { children: shakeAnimation }), "God Mode Activated"] }) })), godModeActive && (_jsx("div", { className: "absolute bottom-2.5 left-[70px] z-[9998] cursor-pointer rounded bg-black bg-opacity-20 px-2.5 py-1 text-[#FF4500]", onClick: () => setGodModeActive(false), children: "Deactivate God Mode" }))] }));
};
export const MemoizedSourContextProvider = React.memo(SourContextProvider);
//# sourceMappingURL=sour-context.js.map